import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AlertModal from 'components/common-components/AlertModal';
import FormButton from 'components/form-components/FormButton';
import TabLayout from 'components/tab-components/TabLayout';
import TabHeader from 'components/tab-components/TabHeader';
import { newScheduleTabList } from '../../utils';
import { CallAPI } from 'actions/General';
import { geoCoordinatesRegex } from 'pages/datasource/utils';
import toast from 'react-hot-toast';
import AdvanceTab from '../scheduleEditModal/AdvanceTab/AdvanceTab';
import { useTranslation } from 'react-i18next';
import { getDisplayValueFromList, priorityOptionsList } from 'components/utils';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import MultiLevelSelect from 'components/form-components/MultiLevelSelect';
import TextArea from 'components/form-components/TextArea';
import TextField from 'components/form-components/TextField';
import DateTimePickerDropdown from 'components/common-components/DateTimePickerDropdown';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';

const ScheduleTasksEditModal = ({ handleClose, refreshList, data }) => {
  const [selectedTab, setSelectedTab] = useState(newScheduleTabList[0]);
  const [values, setValues] = useState({
    referenceNumber: data.referenceNumber || '',
    title: data.title || '',
    description: data.description || '',
    form: data.form?._id || null,
    companyField: data.companyField?._id || null,
    companyFieldRowId: data.companyFieldRowId || null,
    companyFieldValue: data.companyFieldValue || null,
    autoFillCompanyField: data.autoFillCompanyField ?? false,
    // assignedUsers: data.assignedUsers?.map((i) => i._id) || [],
    // assignedGroups: data.assignedGroups?.map((i) => i._id) || [],
    priority: data.priority || '',
    labels: (data.labels || []).map((l) => l._id),
    location: data.location || '',
    scheduleType: 'one_time',
    startDate: data.startDate || '',
    endDate: data.endDate || '',

    // allowOverDue: false,
  });

  const [formsOptions, setFormsOptions] = useState([]);
  const [labelOptions, setLabelOptions] = useState([]);
  const [formCompanyFieldOptionsMap, setFormCompanyFieldOptionsMap] = useState({});
  const [formCompanyFieldValueOptionsMap, setFormCompanyFieldValueOptionsMap] = useState({});
  const [displayValueLookup, setDisplayValueLookup] = useState({});
  // const [allUsers, setAllUsers] = useState([]);
  // const [allGroups, setAllGroups] = useState([]);

  const isValid = () => {
    if (!values.title) {
      toast.error('Title is required');
      return false;
    }

    if (!values.form) {
      toast.error('Form is required');
      return false;
    }

    if (values.companyField && !values.companyFieldRowId) {
      toast.error('Please select company field value');
      return false;
    }

    // if (values.isScheduled) {
    //   if (values.scheduleType === 'one_time') {
    //     if (!values.startDate) {
    //       toast.error('Please select starts time');
    //       return false;
    //     }
    //     if (!values.endDate) {
    //       toast.error('Please select ends time');
    //       return false;
    //     }
    //   }
    // }

    if (values.startDate && values.endDate) {
      const now = new Date();
      const startDate = new Date(values.startDate);
      const endDate = new Date(values.endDate);
      if (startDate <= now) {
        toast.error('Start date must be later than the current date and time');
        return false;
      }
      if (endDate <= startDate) {
        toast.error('End date must be later than the start date');
        return false;
      }
    }

    if (values.location && !geoCoordinatesRegex.test(values.location)) {
      toast.error('Invalid location field format');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!isValid()) return;

    const payload = {
      title: values.title,
      description: values.description,
      form: values.form,
      companyField: values.companyField,
      companyFieldRowId: values.companyFieldRowId,
      companyFieldValue: values.companyFieldValue,
      autoFillCompanyField: values.autoFillCompanyField,
      // assignedUsers: values.assignedUsers,
      // assignedGroups: values.assignedGroups,
      priority: values.priority,
      startDate: values.startDate,
      endDate: values.endDate,
      labels: values.labels,
      location: values.location,
      formVersion: data.formVersion,
    };

    const result = await CallAPI('UPDATE_TASK', payload, data._id);
    if (result.status) {
      refreshList();
    }
    handleClose();
  };

  const loadScheduleDropdownOptions = async () => {
    const result = await CallAPI('GET_SCHEDULE_OPTIONS_LIST');
    if (result.status) {
      const newFormsOptions = [];
      const newFormCompanyFieldOptionsMap = {};
      const newFormCompanyFieldValueOptionsMap = {};
      const newLabelOptions = [];
      const newDisplayValueLookup = {};

      result.data.formsList.forEach((form) => {
        newDisplayValueLookup[form._id] = form.title;
        newFormsOptions.push({
          value: form._id,
          displayValue: form.title,
        });

        newFormCompanyFieldOptionsMap[form._id] = form.companyFields.map((field) => {
          newDisplayValueLookup[field._id] = field.fieldTitle;

          newFormCompanyFieldValueOptionsMap[field._id] = field.values.map((v) => {
            newDisplayValueLookup[v.rowId] = v.value;
            return { value: v.rowId, displayValue: v.value };
          });

          return {
            value: field._id,
            displayValue: field.fieldTitle,
          };
        });
      });

      result.data.labelsList.forEach((l) => {
        newLabelOptions.push({
          value: l._id,
          displayValue: l.name,
        });
      });

      // setAllUsers(result.data.usersList);
      // setAllGroups(result.data.groupsList);

      setFormsOptions(newFormsOptions);
      setFormCompanyFieldOptionsMap(newFormCompanyFieldOptionsMap);
      setFormCompanyFieldValueOptionsMap(newFormCompanyFieldValueOptionsMap);
      setLabelOptions(newLabelOptions);
      setDisplayValueLookup(newDisplayValueLookup);
    }
  };

  useEffect(() => {
    loadScheduleDropdownOptions();
  }, []);

  return (
    <AlertModal
      show={true}
      size='md'
      actionButtons={[]}
      noPadding
      backdropClassName='backdrop-overlap'
    >
      <div className='w-100 px-4 py-0'>
        <TabLayout>
          <TabHeader
            tabList={newScheduleTabList}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            variant='iconTab px-0'
          />
          <>
            <div className='d-flex flex-column gap-3 py-3'>
              {selectedTab.value === 'tabs_advance' ? (
                <AdvanceTab values={values} setValues={setValues} labelOptions={labelOptions} />
              ) : (
                <BasicTab
                  values={values}
                  setValues={setValues}
                  formsOptions={formsOptions}
                  formCompanyFieldOptionsMap={formCompanyFieldOptionsMap}
                  formCompanyFieldValueOptionsMap={formCompanyFieldValueOptionsMap}
                  displayValueLookup={displayValueLookup}
                  // allUsers={allUsers}
                  // allGroups={allGroups}
                />
              )}
            </div>
          </>
        </TabLayout>

        <div className='d-flex justify-content-end gap-3 py-3 border-top-grey-10-h-1'>
          <FormButton text='button_discard' variant='white-1' onClick={handleClose} />
          <FormButton
            text='button_save'
            variant='green-1'
            onClick={handleSave}
            disabled={!data._id}
          />
        </div>
      </div>
    </AlertModal>
  );
};

ScheduleTasksEditModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  refreshList: PropTypes.func.isRequired,
  data: PropTypes.object,
};

export default ScheduleTasksEditModal;

const BasicTab = ({
  values,
  setValues,
  formsOptions,
  formCompanyFieldOptionsMap,
  formCompanyFieldValueOptionsMap,
  displayValueLookup,
  // allUsers,
  // allGroups,
}) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // const displayText = useMemo(() => {
  //   return getAssignmentDisplayValue(
  //     allUsers.filter((u) => values.assignedUsers.includes(u._id)),
  //     allGroups.filter((g) => values.assignedGroups.includes(g._id)),
  //   );
  // }, [values.assignedUsers, values.assignedGroups, allUsers, allGroups]);

  const handleChange = (field, value) => {
    setValues((prevValues) => {
      const updatedValues = { ...prevValues };
      // reset companyField dependent values whenever form changes
      if (field === 'form' && prevValues.form !== value) {
        updatedValues.companyField = null;
        updatedValues.companyFieldRowId = null;
        updatedValues.companyFieldValue = null;
        updatedValues.autoFillCompanyField = false;
      }
      // reset companyField dependent values whenever companyfield changes
      if (field === 'companyField' && prevValues.companyField !== value) {
        updatedValues.companyFieldRowId = null;
        updatedValues.companyFieldValue = null;
      }

      if (field === 'companyFieldRowId' && prevValues.companyFieldRowId !== value) {
        updatedValues.companyFieldValue = displayValueLookup[value] || null;
      }

      updatedValues[field] = value;

      return updatedValues;
    });

    setTouched((prevTouched) => ({ ...prevTouched, [field]: true }));
    setErrors((prevErrors) => ({ ...prevErrors, [field]: undefined }));
  };

  // const handleAssignmentDoneAction = (data) => {
  //   setValues((prev) => ({ ...prev, assignedUsers: data.users, assignedGroups: data.groups }));
  // };

  return (
    <div className='d-flex flex-column gap-3'>
      <span className='px-2 py-1 theme-background-white-3 rounded-md align-self-start'>
        {`TK-${values.referenceNumber}`}
      </span>
      <div className='d-flex flex-column gap-1'>
        <span>{t('field_title')}</span>
        <TextField
          name='title'
          classes='w-auto'
          value={values.title}
          error={errors.title}
          touched={touched.title}
          handleChange={handleChange}
          placeholder='field_placeholder_add_title'
          shrinkFieldOnly
        />
      </div>
      <div className='d-flex flex-column gap-1'>
        <span>{t('field_description')}</span>
        <TextArea
          name='description'
          classes='w-auto'
          value={values.description}
          error={errors.description}
          touched={touched.description}
          handleChange={handleChange}
          placeholder='field_placeholder_write_task_description'
          rows={2}
        />
      </div>
      <div className='w-100 d-flex flex-column gap-1'>
        <span>{t('field_form')}</span>
        <MultiLevelSelect
          name='form'
          options={{ children: formsOptions }}
          value={[]}
          handleChange={(field, value) => handleChange(field, value[0])}
          toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                {displayValueLookup[values.form] ?? t('select_select')}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
          iconVariant={'original'}
          fullWidth
        />
      </div>
      <div className='d-flex gap-3'>
        <div className='w-100 d-flex flex-column gap-1'>
          <span>{t('form_field_company_field')}</span>
          <MultiLevelSelect
            name='companyField'
            options={{ children: formCompanyFieldOptionsMap[values.form] ?? [] }}
            value={[]}
            handleChange={(field, value) => handleChange(field, value[0])}
            toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
            toggleComponent={
              <>
                <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                  {displayValueLookup[values.companyField] ?? t('select_select')}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
            iconVariant={'original'}
            fullWidth
          />
        </div>

        <div className='w-100 d-flex flex-column gap-1'>
          <span>{t('form_value')}</span>
          <MultiLevelSelect
            name='companyFieldRowId'
            options={{ children: formCompanyFieldValueOptionsMap[values.companyField] ?? [] }}
            value={[]}
            handleChange={(field, value) => handleChange(field, value[0])}
            toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
            toggleComponent={
              <>
                <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                  {displayValueLookup[values.companyFieldRowId] ?? t('select_select')}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
            iconVariant={'original'}
            fullWidth
            disabled={!values.companyField}
          />
        </div>
      </div>
      {values.companyField && (
        <SingleCheckBoxField
          name={'autoFillCompanyField'}
          value={values.autoFillCompanyField}
          text={<span>{t('text_auto_fill_company_field')}</span>}
          handleChange={handleChange}
        />
      )}
      <div className='d-flex flex-column gap-1'>
        <span>{t('field_priority')}</span>
        <MultiLevelSelect
          name='priority'
          options={{ children: priorityOptionsList }}
          value={[]}
          handleChange={(field, value) => handleChange(field, value[0])}
          toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                {t(getDisplayValueFromList(priorityOptionsList, values.priority, 'select_select'))}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
          iconVariant={'original'}
          fullWidth
        />
      </div>

      {/* <div>
        <label className='mb-2'>{t('field_assigned_to')}</label>
        <UserAndGroupSelection
          doneAction={handleAssignmentDoneAction}
          toggleComponent={
            <div className='d-flex justify-content-between align-items-center border-grey-1-h-1 cursor-pointer rounded-sm p-2'>
              <div>
                <img src={userGreyIcon} />
                <span className='theme-size-0_9 ms-2'>
                  {displayText ? displayText : t('text_select_user_or_group')}
                </span>
              </div>
              <img src={chevronGreyIcon} className='rotate-180' />
            </div>
          }
          existingUserIds={values.assignedUsers}
          existingGroupIds={values.assignedGroups}
        />
      </div> */}

      <div className='row align-items-center'>
        <div className='col-5 d-flex flex-column'>
          <span className='theme-font-inter-semi-bold theme-text-black-1'>
            {t('field_starts_at')}
          </span>
        </div>
        <div className='col-7'>
          <DateTimePickerDropdown
            name='startDate'
            value={values.startDate}
            onChange={handleChange}
            format12Hour
            defaultTime='startOfDay'
            zIndex={1055}
          />
        </div>
      </div>

      <div className='row align-items-center'>
        <div className='col-5 d-flex flex-column'>
          <span className='theme-font-inter-semi-bold theme-text-black-1'>
            {t('field_ends_at')}
          </span>
        </div>
        <div className='col-7'>
          <DateTimePickerDropdown
            name='endDate'
            value={values.endDate}
            onChange={handleChange}
            format12Hour
            defaultTime='startOfDay'
            zIndex={1055}
          />
        </div>
      </div>
    </div>
  );
};

BasicTab.propTypes = {
  values: PropTypes.object.isRequired,
  setValues: PropTypes.func.isRequired,
  formsOptions: PropTypes.array.isRequired,
  formCompanyFieldOptionsMap: PropTypes.object.isRequired,
  formCompanyFieldValueOptionsMap: PropTypes.object.isRequired,
  displayValueLookup: PropTypes.object.isRequired,
  // allUsers: PropTypes.array.isRequired,
  // allGroups: PropTypes.array.isRequired,
};
